import React, { MouseEvent, useContext, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import Popover from '@mui/material/Popover';
import { Link } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import classNames from 'classnames';
import Context from '../../context';
import useKeepitalRoutes from '../../components/_hook/useKeepitalRoutes';
import useRoutes from '../../components/_hook/useRoutes';
import KeepitalAccountAppSvg from '../../components/_svg/KeepitalAccountAppSvg';
import KeepitalKeepitalAppSvg from '../../components/_svg/KeepitalKeepitalAppSvg';
import KeepitalAnnounceAppSvg from '../../components/_svg/KeepitalAnnounceAppSvg';
import KeepitalSuperAppSvg from '../../components/_svg/KeepitalSuperAppSvg';
import KeepitalMinisiteAppSvg from '../../components/_svg/KeepitalMinisiteAppSvg';
import KeepitalProcurementAppSvg from '../../components/_svg/KeepitalProcurementAppSvg';
import KeepitalBusinessAppSvg from '../../components/_svg/KeepitalBusinessAppSvg';
import KeepitalProductAppSvg from '../../components/_svg/KeepitalProductAppSvg';
import KeepitalCodeAppSvg from '../../components/_svg/KeepitalCodeAppSvg';
import KeepitalMessengerAppSvg from '../../components/_svg/KeepitalMessengerAppSvg';
import KeepitalPlanAppSvg from '../../components/_svg/KeepitalPlanAppSvg';
import KeepitalLogAppSvg from '../../components/_svg/KeepitalLogAppSvg';
import useRouter from '../../components/_hook/useRouter';
import useSuperRoutes from '../../components/_hook/useSuperRoutes';
import KeepitalSubdirectoryAppSvg from '../../components/_svg/KeepitalSubdirectoryAppSvg';
import SuperAdminPermission from '../../adminComponents/_permission/SuperAdminPermission';

interface IProps {
  iconOnly?: boolean;
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  button: {
    borderRadius: '50px',
  },
  popover: {
    maxWidth: '400px',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'calc(100% - 32px);',
    },
    '& a': {
      textDecoration: 'none',
      textTransform: 'capitalize',
      color: theme.palette.mode === 'light' ? 'black' : 'white',
    },
    '& svg': {
      marginBottom: theme.spacing(1),
    },
  },
  appItemContainer: {
    padding: '8px',
  },
}));

export default function HeaderAppButton(props: IProps) {
  const { iconOnly } = props;
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const { t } = useTranslation();
  const { user, isSuperAdmin, company } = useContext(Context);
  const { path } = useRoutes();
  const { path: keepitalPath } = useKeepitalRoutes();
  const isMenuOpen = Boolean(anchorEl);
  const { routeName } = useRouter();
  const currentApp = routeName.split('.')[0];
  const userCompanies = user.companies || [];
  const { path: superPath } = useSuperRoutes();

  const appList = [
    {
      path: keepitalPath('user.dashboard', { userID: user._id }),
      appName: 'account',
      icon: <KeepitalAccountAppSvg width={64} height={64} />,
      text: t('apps_account'),
      redirect: true,
    },
    {
      path: path('messenger.chatrooms', {
        companyID: company ? company._id : userCompanies[0].id,
      }),
      appName: 'messenger',
      icon: <KeepitalMessengerAppSvg width={64} height={64} />,
      text: t('apps_messenger'),
    },
    {
      path: path('announce.dashboard', {
        companyID: company ? company._id : userCompanies[0].id,
      }),
      appName: 'announce',
      icon: <KeepitalAnnounceAppSvg width={64} height={64} />,
      text: t('apps_announce'),
    },
    {
      path: path('procurement.dashboard', {
        companyID: company ? company._id : userCompanies[0].id,
      }),
      appName: 'procurement',
      icon: <KeepitalProcurementAppSvg width={64} height={64} />,
      text: t('apps_procurement'),
    },
    ...(company || userCompanies.length
      ? [
          {
            path: path('business.dashboard', {
              companyID: company ? company._id : userCompanies[0].id,
            }),
            appName: 'business',
            icon: <KeepitalBusinessAppSvg width={64} height={64} />,
            text: t('apps_business'),
          },
          {
            path: path('product.dashboard', {
              companyID: company ? company._id : userCompanies[0].id,
            }),
            appName: 'product',
            icon: <KeepitalProductAppSvg width={64} height={64} />,
            text: t('apps_product'),
          },
          {
            path: path('minisite.dashboard', {
              companyID: company ? company._id : userCompanies[0].id,
            }),
            appName: 'minisite',
            icon: <KeepitalMinisiteAppSvg width={64} height={64} />,
            text: t('apps_minisite'),
          },
          {
            path: path('subdirectory.dashboard', {
              companyID: company ? company._id : userCompanies[0].id,
            }),
            appName: 'subdirectory',
            icon: <KeepitalSubdirectoryAppSvg width={56} height={56} />,
            text: t('apps_subdirectory'),
            isSuperAdmin: true,
          },
          {
            path: path('code.dashboard', {
              companyID: company ? company._id : userCompanies[0].id,
            }),
            appName: 'code',
            icon: <KeepitalCodeAppSvg width={64} height={64} />,
            text: t('apps_code'),
          },
          {
            path: path('plan.dashboard', {
              companyID: company ? company._id : userCompanies[0].id,
            }),
            appName: 'plans',
            icon: <KeepitalPlanAppSvg width={64} height={64} />,
            text: t('apps_plans'),
          },
        ]
      : []),

    {
      path: superPath('keepital.dashboard'),
      appName: 'keepital',
      icon: <KeepitalKeepitalAppSvg width={64} height={64} />,
      text: t('apps_keepital'),
      redirect: true,
      isSuperAdmin: true,
    },
    {
      path: superPath('super.dashboard'),
      appName: 'super',
      icon: <KeepitalSuperAppSvg width={64} height={64} />,
      text: t('apps_super'),
      redirect: true,
      isSuperAdmin: true,
    },
    {
      path: superPath('log.dashboard'),
      appName: 'log',
      icon: <KeepitalLogAppSvg width={64} height={64} />,
      text: t('log'),
      redirect: true,
      isSuperAdmin: true,
    },
  ];

  const handleMenuOpen = (event: MouseEvent) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuId = 'primary-app-menu';
  const renderMenu = (
    <Popover
      anchorEl={anchorEl}
      id={menuId}
      keepMounted
      open={isMenuOpen}
      onClose={handleMenuClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      classes={{
        paper: classes.popover,
      }}
    >
      <Grid container item xs={12} spacing={1}>
        {appList.map((app) => {
          if (app.isSuperAdmin && !isSuperAdmin) {
            return null;
          }

          const ButtonComponent = app.redirect ? (
            <Button fullWidth>
              <a href={app.path} rel="noopener noreferrer" target="_blank">
                {app.icon}
                <Typography variant="subtitle1">{app.text}</Typography>
              </a>
            </Button>
          ) : (
            <Button fullWidth>
              <Link to={app.path} onClick={handleMenuClose}>
                {app.icon}
                <Typography variant="subtitle1">{app.text}</Typography>
              </Link>
            </Button>
          );

          return (
            <Grid
              className={classes.appItemContainer}
              container
              justifyContent="center"
              item
              md={3}
              sm={4}
              xs={6}
              key={app.appName}
            >
              {app.isSuperAdmin ? (
                <SuperAdminPermission>{ButtonComponent}</SuperAdminPermission>
              ) : (
                ButtonComponent
              )}
            </Grid>
          );
        })}
      </Grid>
    </Popover>
  );

  const renderDesktopIconButton = (
    <IconButton
      className={classNames(
        classes.button,
        'default-layout-header-app-button-joyride'
      )}
      aria-controls={menuId}
      aria-haspopup="true"
      onClick={handleMenuOpen}
      color="inherit"
      size="large"
    >
      {currentApp === 'account' && (
        <KeepitalAccountAppSvg width={24} height={24} />
      )}
      {currentApp === 'messenger' && (
        <KeepitalMessengerAppSvg width={24} height={24} />
      )}
      {currentApp === 'announce' && (
        <KeepitalAnnounceAppSvg width={24} height={24} />
      )}
      {currentApp === 'procurement' && (
        <KeepitalProcurementAppSvg width={24} height={24} />
      )}
      {currentApp === 'business' && (
        <KeepitalBusinessAppSvg width={24} height={24} />
      )}
      {currentApp === 'product' && (
        <KeepitalProductAppSvg width={24} height={24} />
      )}
      {currentApp === 'minisite' && (
        <KeepitalMinisiteAppSvg width={24} height={24} />
      )}
      {currentApp === 'subdirectory' && (
        <KeepitalSubdirectoryAppSvg width={24} height={24} />
      )}
      {currentApp === 'code' && <KeepitalCodeAppSvg width={24} height={24} />}
      {currentApp === 'plan' && <KeepitalPlanAppSvg width={24} height={24} />}
      {currentApp === 'keepital' && (
        <KeepitalKeepitalAppSvg width={24} height={24} />
      )}
      {currentApp === 'super' && <KeepitalSuperAppSvg width={24} height={24} />}
      {currentApp === 'log' && <KeepitalLogAppSvg width={24} height={24} />}
      {!iconOnly && (
        <>
          &nbsp;&nbsp;
          <Typography variant="body1">
            {t(`apps_${currentApp}${currentApp === 'plan' ? 's' : ''}`)}
          </Typography>
          <ArrowDropDownIcon />
        </>
      )}
    </IconButton>
  );

  return (
    <>
      {renderDesktopIconButton}
      {renderMenu}
    </>
  );
}
